import React, { useState, useMemo } from 'react';

import { Form } from 'antd';
import { useNavigate } from '@common/hooks';
import moment from 'moment';

import {
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  ErrorHandler,
} from '@common/components';

import { Colors, FontWeight } from '@common/tokens';
import { ShiftRequestDetailsContent } from '@scheduling/components';

import { MessageTypeSelector } from './_message-type-selector';
import { IndividualRecipientsSelector } from './_individual-recipients-selector';
import { RecipientsList } from './_recipients-list';
import { WorkforceGroupSelector } from './_workforce-group-selector';
import { NoteTextArea } from './_note-text-area';
import {
  MessageType,
  useShiftGapData,
  useCreateSpearfishingMutation,
  useSpearfishingData,
} from './_hooks';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';

export type SpearfishableGroup = {
  spearfishableGroupsByShiftGap: {
    id: string;
    name: string;
    count: number;
    custom: boolean;
    spearfishable: boolean;
  }[];
};

export function SendShiftDrawer({
  open,
  onClose,
  shiftId = '',
  from = '',
  type = '',
}: {
  open: boolean;
  onClose: () => void;
  shiftId?: string;
  from?: string;
  type?: string;
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [message, setMessage] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [workerIds, setWorkerIds] = useState<string[]>([]);
  const [selectedMessageType, setSelectedMessageType] =
    useState<MessageType>('individuals');

  const currentMembership = useCurrentMembership();

  const groupMessagingEnabled = currentMembership.canShowGroupShiftMessaging;

  const {
    spearfishingDataLoading,
    individualsSpearfishableWorkerData,
    homeUnitSpearfishableWorkerData,
    messageTypeData,
    spearfishableGroupData,
    preferenceMatchSpearfishableWorkerData,
  } = useSpearfishingData({ shiftId, groupMessagingEnabled });

  const { shiftGapData, shiftGapLoading, shiftGapError } = useShiftGapData({
    shiftId,
    sourcePage: type,
  });

  function buildShiftsCalendarDetailsDrawerRoute() {
    const date = moment
      .parseZone(shiftGapData?.startDatetime)
      .tz(shiftGapData?.timeZone || '')
      .format('YYYY-MM-DD');

    return buildDialogUrl('shifts-calendar-details-drawer', {
      props: { date },
    });
  }

  const shiftDetailsDrawerUrl = buildDialogUrl(
    'shifts-request-details-drawer',
    {
      props: {
        shiftId,
        type,
      },
    }
  );

  const onDrawerClose = () => {
    onClose();
    setTimeout(() => {
      setWorkerIds([]);
      setMessage('');
      setSelectedMessageType('individuals');
      form.resetFields();
    }, 300);
  };

  const onCloseWithRedirect = () => {
    onDrawerClose();

    if (from === 'list' && type === 'calendar') {
      navigate(buildShiftsCalendarDetailsDrawerRoute());
    } else {
      navigate(shiftDetailsDrawerUrl);
    }
  };

  const { createSpearfishing, createLoading } = useCreateSpearfishingMutation({
    onClose: onCloseWithRedirect,
  });

  const isLoading = spearfishingDataLoading || shiftGapLoading;

  const hasError = useMemo(() => {
    return (
      message.length > 150 ||
      (workerIds.length === 0 && groupIds.length === 0) ||
      (selectedMessageType === 'home_unit' && message.length === 0) ||
      (selectedMessageType === 'workforce_groups' && message.length === 0)
    );
  }, [message.length, workerIds.length, groupIds.length, selectedMessageType]);

  return (
    <>
      <Drawer
        open={open}
        title="Send Shift"
        className="shift-request-details-drawer"
        onClose={onClose}
        loading={isLoading}
        footer={
          <ButtonGroup>
            <Button onClick={onClose} disabled={createLoading}>
              Cancel
            </Button>
            <Button
              primary
              disabled={createLoading || hasError}
              onClick={() => {
                createSpearfishing({
                  variables: {
                    workerIds: workerIds,
                    shiftGapId: shiftId,
                    message,
                    messageType: selectedMessageType,
                    groupIds,
                  },
                });
              }}
            >
              Send
            </Button>
          </ButtonGroup>
        }
      >
        {shiftGapError ? (
          <ErrorHandler error={shiftGapError} log="SendShiftDrawer" />
        ) : (
          <>
            {shiftGapData && (
              <ShiftRequestDetailsContent
                shift={shiftGapData!}
                showOpeningCount
              />
            )}
            <Divider gutter fullWidth={false} />
            <Form
              layout="vertical"
              form={form}
              initialValues={{ messageType: 'individuals' }}
            >
              <MessageTypeSelector
                groupMessagingEnabled={groupMessagingEnabled}
                homeUnitSpearfishableWorkerData={
                  homeUnitSpearfishableWorkerData
                }
                preferenceMatchSpearfishableWorkerData={
                  preferenceMatchSpearfishableWorkerData
                }
                form={form}
                selectedMessageType={selectedMessageType}
                setSelectedMessageType={setSelectedMessageType}
                setWorkerIds={setWorkerIds}
                setGroupIds={setGroupIds}
                messageTypeData={messageTypeData || []}
              />
              {selectedMessageType === 'individuals' && (
                <IndividualRecipientsSelector
                  workerIds={workerIds}
                  setWorkerIds={setWorkerIds}
                  individualSpearfishableWorkerData={
                    individualsSpearfishableWorkerData
                  }
                />
              )}
              {selectedMessageType === 'home_unit' && (
                <RecipientsList
                  spearfishableWorkerData={homeUnitSpearfishableWorkerData}
                />
              )}
              {selectedMessageType === 'preference_match' && (
                <RecipientsList
                  spearfishableWorkerData={
                    preferenceMatchSpearfishableWorkerData
                  }
                />
              )}
              {selectedMessageType === 'workforce_groups' && (
                <WorkforceGroupSelector
                  groupIds={groupIds}
                  setGroupIds={setGroupIds}
                  spearfishableGroupData={spearfishableGroupData}
                />
              )}
              <NoteTextArea
                selectedMessageType={selectedMessageType}
                createLoading={createLoading}
                setMessage={setMessage}
                message={message}
              />
            </Form>
          </>
        )}
      </Drawer>
      <style jsx>{`
        :global(.ant-select-item-option-disabled) {
          color: ${Colors.midnightLighter};
        }
        :global(.ant-select-item-group) {
          color: #000;
          font-weight: ${FontWeight.medium};
        }
        :global(.count-pill) {
          background-color: ${Colors.neutralLow};
          border-color: ${Colors.neutralLow};
          color: black;
          font-weight: ${FontWeight.light};
          padding: 0 8px;
          margin-left: 8px;
          border-radius: 8px;
        }
      `}</style>
    </>
  );
}
